/**
 * SecretaryDeskPage — Workspace tổng hợp cho Thư ký.
 *
 * 4 tabs:
 *   1. Phiên hôm nay         — courtrooms ACTIVE / sắp diễn ra trong 24h
 *   2. Biên bản chờ          — minutes status=draft
 *   3. Disclosure pending    — rounds có disclosure_at <= now nhưng chưa done
 *   4. Bảng phân vai chờ     — teams chưa nộp role sheet cho round Bán kết
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Typography,
  Stack,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
} from '@mui/material'
import {
  Gavel,
  EditNote,
  Schedule,
  AssignmentInd,
  VideoCall,
  CheckCircle,
  Warning,
} from '@mui/icons-material'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx } from '../theme/sectionSurfaces'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCourtroomByRoundQuery } from '../store/api/courtroomsApi'
import { useListMinutesForCaseQuery } from '../store/api/hearingMinutesApi'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'

// 2026-05-25: polling 15s — Secretary desk là dashboard, cần data tươi sau
// khi admin publish/end courtroom ở tab khác. Trước đây fetch 1 lần on mount
// → UI stale, user thấy "Đang diễn ra" sau khi đã end phiên.
const POLL_MS = 15000

interface RoundLite {
  id: string
  name: string
  case_id: string
  round_type: string
  start_date?: string
  end_date?: string
  status?: string
  disclosure_at?: string
  disclosure_done?: boolean
}

interface CaseLite {
  id: string
  title: string
  current_round_id?: string
  assigned_team_ids?: string[]
}

interface TeamLite {
  id: string
  name: string
  competition_status?: string
}

function formatDt(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function SecretaryDeskPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState(0)

  const { data: cases = [], isLoading: l1 } = useGetCasesQuery(undefined, {
    pollingInterval: POLL_MS,
  })
  const { data: rounds = [], isLoading: l2 } = useGetRoundsQuery(undefined, {
    pollingInterval: POLL_MS,
  })
  const { data: teams = [], isLoading: l3 } = useGetTeamsQuery(undefined, {
    pollingInterval: POLL_MS,
  })

  const casesById = useMemo(() => {
    const m = new Map<string, CaseLite>()
    ;(cases as any[]).forEach((c) => m.set(c.id, c))
    return m
  }, [cases])

  const teamsById = useMemo(() => {
    const m = new Map<string, TeamLite>()
    ;(teams as any[]).forEach((t) => m.set(t.id, t))
    return m
  }, [teams])

  const now = Date.now()
  const in24h = now + 24 * 3600_000

  // Tab 1: Phiên hôm nay (rounds ACTIVE hoặc start_date trong 24h)
  const todayHearings = useMemo(() => {
    return (rounds as RoundLite[])
      .filter((r) => {
        if (r.round_type !== 'semifinal' && r.round_type !== 'final') return false
        const start = r.start_date ? new Date(r.start_date).getTime() : 0
        return r.status === 'active' || (start > 0 && start <= in24h && start >= now - 6 * 3600_000)
      })
      .sort((a, b) => (new Date(a.start_date || 0).getTime() - new Date(b.start_date || 0).getTime()))
  }, [rounds, now, in24h])

  // Tab 3: Disclosure pending
  const disclosurePending = useMemo(() => {
    return (rounds as RoundLite[])
      .filter((r) => {
        if (r.round_type !== 'semifinal' && r.round_type !== 'final') return false
        if (!r.disclosure_at) return false
        if (r.disclosure_done) return false
        const at = new Date(r.disclosure_at).getTime()
        return at <= in24h
      })
      .sort((a, b) => new Date(a.disclosure_at || 0).getTime() - new Date(b.disclosure_at || 0).getTime())
  }, [rounds, in24h])

  // Tab 4: Bảng phân vai chờ — teams advanced + chưa có sheet.
  // 2026-05-25: bao gồm cả `advanced_to_final` (đội R3) — trước đây chỉ
  // check `advanced_to_semifinal` → tab rỗng sau khi đội thắng Bán kết.
  const roleSheetPending = useMemo(() => {
    const items: Array<{ team: TeamLite; case: CaseLite; round: RoundLite }> = []
    ;(rounds as RoundLite[]).forEach((r) => {
      if (r.round_type !== 'semifinal' && r.round_type !== 'final') return
      const c = casesById.get(r.case_id)
      if (!c?.assigned_team_ids) return
      c.assigned_team_ids.forEach((tid) => {
        const t = teamsById.get(tid)
        if (!t) return
        const advanced =
          (r.round_type === 'semifinal' &&
            (t.competition_status === 'advanced_to_semifinal' ||
              t.competition_status === 'advanced_to_final')) ||
          (r.round_type === 'final' && t.competition_status === 'advanced_to_final')
        if (advanced) {
          items.push({ team: t, case: c, round: r })
        }
      })
    })
    return items
  }, [rounds, casesById, teamsById])

  const isLoading = l1 || l2 || l3

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Bàn Thư ký"
        subtitle="Không gian làm việc tổng hợp các tác vụ thư ký phiên Bán kết & Chung kết"
        icon={<Gavel />}
      />

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 0 })}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<VideoCall fontSize="small" />}
            iconPosition="start"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Phiên hôm nay</span>
                <Chip size="small" color="primary" label={todayHearings.length} />
              </Stack>
            }
          />
          <Tab
            icon={<EditNote fontSize="small" />}
            iconPosition="start"
            label="Biên bản phiên"
          />
          <Tab
            icon={<Schedule fontSize="small" />}
            iconPosition="start"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Disclosure</span>
                {disclosurePending.length > 0 && (
                  <Chip size="small" color="warning" label={disclosurePending.length} />
                )}
              </Stack>
            }
          />
          <Tab
            icon={<AssignmentInd fontSize="small" />}
            iconPosition="start"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Bảng phân vai</span>
                <Chip size="small" label={roleSheetPending.length} />
              </Stack>
            }
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Tab 1: Phiên hôm nay */}
              {tab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Phiên xét xử hôm nay & sắp diễn ra
                  </Typography>
                  {todayHearings.length === 0 ? (
                    <Alert severity="info">Không có phiên nào trong 24h tới.</Alert>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cuộc thi</TableCell>
                          <TableCell>Vòng</TableCell>
                          <TableCell>Bắt đầu</TableCell>
                          <TableCell>Trạng thái</TableCell>
                          <TableCell align="right">Thao tác</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {todayHearings.map((r) => (
                          <HearingRow
                            key={r.id}
                            round={r}
                            caseTitle={
                              casesById.get(r.case_id)?.title || '— Cuộc thi chưa rõ —'
                            }
                            onEnter={() => navigate('/courtroom')}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {/* Tab 2: Biên bản phiên — list minutes thật từ /hearing-minutes/case.
                  2026-05-25: trước đây tab này chỉ là Alert tĩnh, không phản
                  ánh được biên bản đã đóng băng. Giờ chọn case → list các
                  minute (draft + finalized) + entries_count + nút mở phòng. */}
              {tab === 1 && (
                <MinutesTab
                  cases={cases as CaseLite[]}
                  navigate={navigate}
                />
              )}

              {/* Tab 3: Disclosure pending */}
              {tab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Lịch trình công bố
                  </Typography>
                  {disclosurePending.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircle />}>
                      Không có disclosure nào pending. Cron `POST /notifications/deadline-reminders/run`
                      sẽ tự công bố khi đến hạn.
                    </Alert>
                  ) : (
                    <>
                      <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                        Có {disclosurePending.length} round có disclosure_at sắp đến hoặc quá hạn —
                        cron sẽ tự xử lý lần chạy kế tiếp.
                      </Alert>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cuộc thi</TableCell>
                            <TableCell>Vòng</TableCell>
                            <TableCell>Disclosure at</TableCell>
                            <TableCell>Trạng thái</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {disclosurePending.map((r) => {
                            const at = r.disclosure_at ? new Date(r.disclosure_at).getTime() : 0
                            const overdue = at < now
                            return (
                              <TableRow key={r.id} hover>
                                <TableCell>{casesById.get(r.case_id)?.title}</TableCell>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>{formatDt(r.disclosure_at)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    color={overdue ? 'error' : 'warning'}
                                    label={overdue ? 'Quá hạn — chờ cron' : 'Sắp đến'}
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </Box>
              )}

              {/* Tab 4: Bảng phân vai */}
              {tab === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Bảng phân vai — Đội cần theo dõi
                  </Typography>
                  {roleSheetPending.length === 0 ? (
                    <Alert severity="info">
                      Chưa có đội nào được công bố vào Bán kết. Sau khi BTC publish kết quả Sơ loại,
                      các đội Top 4 sẽ xuất hiện ở đây.
                    </Alert>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Đội</TableCell>
                          <TableCell>Cuộc thi</TableCell>
                          <TableCell>Vòng</TableCell>
                          <TableCell>Trạng thái</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roleSheetPending.map((it, idx) => (
                          <TableRow key={`${it.team.id}_${it.round.id}_${idx}`} hover>
                            <TableCell>{it.team.name}</TableCell>
                            <TableCell>{it.case.title}</TableCell>
                            <TableCell>{it.round.name}</TableCell>
                            <TableCell>
                              <Chip size="small" color="success" label="Top 4" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Container>
  )
}

// ════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════

/** HearingRow — render 1 row trong "Phiên hôm nay" với status từ courtroom
 *  (KHÔNG phải từ round). Lý do: round.status='active' không update khi
 *  admin "Kết thúc phiên" — chỉ courtroom.status đổi sang 'ended'. */
function HearingRow({
  round,
  caseTitle,
  onEnter,
}: {
  round: RoundLite
  caseTitle: string
  onEnter: () => void
}) {
  const { data: courtroom } = useGetCourtroomByRoundQuery(round.id, {
    pollingInterval: POLL_MS,
  })
  const statusValue = (courtroom as any)?.status || round.status || ''
  const label =
    statusValue === 'ended'
      ? 'Đã kết thúc'
      : statusValue === 'active'
      ? 'Đang diễn ra'
      : statusValue === 'scheduled'
      ? 'Đã lên lịch'
      : statusValue === 'cancelled'
      ? 'Đã huỷ'
      : 'Sắp diễn ra'
  const color: 'success' | 'default' | 'error' | 'info' =
    statusValue === 'active'
      ? 'success'
      : statusValue === 'ended'
      ? 'default'
      : statusValue === 'cancelled'
      ? 'error'
      : 'info'
  return (
    <TableRow hover>
      <TableCell>{caseTitle}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={round.round_type === 'final' ? 'Chung kết' : 'Bán kết'}
          color={round.round_type === 'final' ? 'secondary' : 'primary'}
        />
      </TableCell>
      <TableCell>{formatDt(round.start_date)}</TableCell>
      <TableCell>
        <Chip size="small" label={label} color={color} />
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          variant={statusValue === 'ended' ? 'outlined' : 'contained'}
          startIcon={<VideoCall />}
          onClick={onEnter}
        >
          {statusValue === 'ended' ? 'Xem lại' : 'Vào phòng'}
        </Button>
      </TableCell>
    </TableRow>
  )
}

/** MinutesTab — list biên bản thật của case đã chọn. Hiển thị status
 *  draft/finalized, số entries, link sang phòng xử. */
function MinutesTab({
  cases,
  navigate,
}: {
  cases: CaseLite[]
  navigate: ReturnType<typeof useNavigate>
}) {
  const [selectedCaseId, setSelectedCaseId] = useState<string>(cases[0]?.id || '')
  const { data: minutes = [], isFetching } = useListMinutesForCaseQuery(selectedCaseId, {
    skip: !selectedCaseId,
    pollingInterval: POLL_MS,
  })

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Biên bản phiên
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Mỗi phiên có 1 biên bản (HearingMinute). Mở/sửa trực tiếp trong phòng
        xét xử. Sau phiên, thư ký bấm <b>"Đóng băng biên bản"</b> →{' '}
        <code>status='finalized'</code> + audit log.
      </Alert>
      <FormControl size="small" sx={{ minWidth: 320, mb: 2 }}>
        <InputLabel>Cuộc thi</InputLabel>
        <Select
          value={selectedCaseId}
          label="Cuộc thi"
          onChange={(e) => setSelectedCaseId(e.target.value)}
        >
          {cases.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isFetching && !minutes.length ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !selectedCaseId ? (
        <Alert severity="info">Chọn cuộc thi để xem danh sách biên bản.</Alert>
      ) : minutes.length === 0 ? (
        <Alert severity="info">
          Chưa có biên bản nào cho cuộc thi này. Biên bản được tạo tự động khi
          thư ký mở trong phiên toà ảo.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Phiên (round)</TableCell>
              <TableCell align="center">Trạng thái</TableCell>
              <TableCell align="right">Số entries</TableCell>
              <TableCell>Bắt đầu</TableCell>
              <TableCell>Đóng băng</TableCell>
              <TableCell align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {minutes.map((m) => {
              const isFrozen = m.status === 'finalized'
              return (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <code>{m.round_id?.slice(0, 8)}…</code>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={isFrozen ? 'Đã đóng băng' : 'Nháp (đang ghi)'}
                      color={isFrozen ? 'success' : 'warning'}
                      icon={isFrozen ? <CheckCircle fontSize="small" /> : <EditNote fontSize="small" />}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {m.entries?.length ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDt(m.started_at)}</TableCell>
                  <TableCell>{isFrozen ? formatDt(m.finalized_at) : '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VideoCall />}
                      onClick={() => navigate('/courtroom')}
                    >
                      {isFrozen ? 'Xem' : 'Tiếp tục ghi'}
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
